import { create } from 'zustand'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number // ms, 0 = persistent
  action?: {
    label: string
    onClick: () => void
  }
  createdAt: number
}

interface NotificationState {
  notifications: Notification[]
  add: (notification: Omit<Notification, 'id' | 'createdAt'>) => string
  remove: (id: string) => void
  clear: () => void
}

let notificationId = 0

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],

  add: (notification) => {
    const id = `notification-${++notificationId}`
    const newNotification: Notification = {
      ...notification,
      id,
      createdAt: Date.now(),
      duration: notification.duration ?? 5000, // Default 5 seconds
    }

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }))

    // Auto-remove after duration (if not persistent)
    if ((newNotification.duration ?? 0) > 0) {
      setTimeout(() => {
        get().remove(id)
      }, newNotification.duration)
    }

    return id
  },

  remove: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }))
  },

  clear: () => {
    set({ notifications: [] })
  },
}))

// Helper functions for common notification types
export const notify = {
  success: (title: string, message?: string, options?: Partial<Notification>) =>
    useNotificationStore.getState().add({ type: 'success', title, message, ...options }),

  error: (title: string, message?: string, options?: Partial<Notification>) =>
    useNotificationStore.getState().add({ type: 'error', title, message, duration: 0, ...options }),

  warning: (title: string, message?: string, options?: Partial<Notification>) =>
    useNotificationStore.getState().add({ type: 'warning', title, message, ...options }),

  info: (title: string, message?: string, options?: Partial<Notification>) =>
    useNotificationStore.getState().add({ type: 'info', title, message, ...options }),
}
